import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import _debounce from "lodash/debounce";
import { FoodSearchResults } from "./FoodSearchResults";
import SaveInputButtons from "../../../../../parts/SaveInputButtons";
import {
  completeVolumeUnits,
  completeWeightUnits,
  weightUnits,
  calculateMeasurementPerUnit,
  getFilteredFoods,
} from "../../../../../utils/nutrition-util";
import Select from "../../../../../parts/inputs/select";
import { getItemsToAdd } from "../../../../../utils/nutrition-helpers";

export const FoodActionsForBuildMeal = ({
  locals,
  searchFilter,
  api,
  onAddItem,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const { customFoods = [], activeUserToRequest } = locals;
  const { mealPlanDataSources = ["usda", "afcd", "verified-food"] } =
    activeUserToRequest;
  const initialState = {
    searchValue: "",
    isSearching: false,
    isLoading: false,
    error: null,
    selectedItem: null,
    quantity: 0,
    unitOfMeasure: "Select",
    unitOfMeasureOptions: [],
    genericFoods: [],
    hasMore: false,
    nextVerifiedOffset: 0,
    nextGeneralOffset: 0,
    isLoadingMore: false,
  };
  const [state, setState] = useState(initialState);
  const containerRef = useRef(null);
  const {
    searchValue,
    isSearching,
    isLoading,
    error,
    selectedItem,
    quantity,
    unitOfMeasure,
    unitOfMeasureOptions,
    genericFoods,
    hasMore,
    nextVerifiedOffset,
    nextGeneralOffset,
    isLoadingMore,
  } = state;

  /**
   * Filter the custom food data
   */
  const data = useMemo(() => {
    const filterFn = ({ name }) => {
      return searchValue
        ? name.toLowerCase().includes(searchValue.toLowerCase())
        : true;
    };
    const filteredCustom = customFoods.filter(filterFn);

    const order = ["food", "recipe", "meal"];
    const orderedCustom = order.flatMap((t) =>
      filteredCustom.filter((f) => f.type === t)
    );

    return [...orderedCustom, ...(genericFoods || [])];
  }, [searchValue, customFoods, genericFoods]);

  /**
   * Function to avoid strings in number input
   * @param {*} e
   */
  const handleKeyDown = (e) => {
    if (["e", "E", "-", "+", ","].includes(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * Function to update the states
   * @param {*} newState
   * @returns
   */
  const updateState = (newState) =>
    setState((prevState) => ({ ...prevState, ...newState }));

  /**
   * Reset the search value
   * @param {*} param0
   */
  const handleResetInputSearch = (e) => {
    e.preventDefault();
    updateState({
      isSearching: false,
      searchValue: "",
      error: null,
      selectedItem: null,
      quantity: 0,
      unitOfMeasure: "Select",
      unitOfMeasureOptions: [],
    });
  };

  /**
   * Handle search when the user is typing
   */
  const debouncedSearch = useRef(
    _debounce(async (searchValue) => {
      if (!mealPlanDataSources || mealPlanDataSources.length === 0) {
        return;
      }

      updateState({ isLoading: true, error: null });
      try {
        const result = await getFilteredFoods(
          searchValue,
          mealPlanDataSources,
          api
        );
        updateState({
          isLoading: false,
          genericFoods: result.data,
          hasMore: result.hasMore,
          nextVerifiedOffset: result.nextVerifiedOffset,
          nextGeneralOffset: result.nextGeneralOffset,
        });
      } catch (err) {
        console.error(err);
        updateState({
          isLoading: false,
          error: t("athlete-portal:foodActions.failedToFetch"),
        });
      }
    }, 1000)
  );

  /**
   * UseEffect to close the search icons if clicked outside
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        updateState({ isSearching: false });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /**
   * Debounce handleSearch
   */

  useEffect(() => {
    if (searchValue.length > 1) {
      debouncedSearch.current(searchValue);
    } else if (genericFoods.length > 0) {
      updateState({
        genericFoods: [],
        hasMore: false,
        nextVerifiedOffset: 0,
        nextGeneralOffset: 0,
      });
    }
  }, [searchValue]);

  /**
   * Cleanup function to cancel the debounce when the component unmounts
   */
  useEffect(() => {
    return () => {
      if (debouncedSearch.current) {
        debouncedSearch.current.cancel();
      }
    };
  }, []);

  /**
   * submit the search
   * @param {*} param0
   */

  const handleSearch = async (e) => {
    e.preventDefault();
    if (mealPlanDataSources.length === 0) {
      return;
    }
    updateState({ isLoading: true, error: null });
    try {
      const result = await getFilteredFoods(
        searchValue,
        mealPlanDataSources,
        api
      );
      updateState({
        isLoading: false,
        genericFoods: result.data,
        hasMore: result.hasMore,
        nextVerifiedOffset: result.nextVerifiedOffset,
        nextGeneralOffset: result.nextGeneralOffset,
      });
    } catch (err) {
      console.error(err);
      updateState({
        isLoading: false,
        error: "Failed to fetch data. Try again.",
      });
    }
  };

  /**
   *
   * @returns Load more generic search results (pagination)
   */
  const handleLoadMore = async () => {
    if (!hasMore || isLoadingMore) return;
    updateState({ isLoadingMore: true });
    try {
      const result = await getFilteredFoods(
        searchValue,
        mealPlanDataSources,
        api,
        {
          verifiedOffset: nextVerifiedOffset,
          generalOffset: nextGeneralOffset,
        }
      );
      updateState({
        genericFoods: [...genericFoods, ...result.data],
        hasMore: result.hasMore,
        nextVerifiedOffset: result.nextVerifiedOffset,
        nextGeneralOffset: result.nextGeneralOffset,
        isLoadingMore: false,
      });
    } catch (err) {
      console.error(err);
      updateState({ isLoadingMore: false });
    }
  };

  /**
   * Select the item from the search bar
   * @param {*} item
   */
  const handleItemSelect = (item) => {
    const {
      unitOfMeasure,
      quantityDescription,
      name,
      quantityNumber,
      measurementsValue,
    } = item;

    const unitOfMeasureForDefaultMeasure = unitOfMeasure || "g";

    const defaultMeasure = {
      value: "custom",
      text: `${quantityDescription} - ${calculateMeasurementPerUnit(
        quantityNumber,
        measurementsValue
      )} ${unitOfMeasureForDefaultMeasure}`,
    };

    let optionsToSelect = !unitOfMeasure
      ? [defaultMeasure]
      : weightUnits.includes(unitOfMeasure)
        ? [defaultMeasure, ...completeWeightUnits]
        : [defaultMeasure, ...completeVolumeUnits];

    updateState({
      selectedItem: item,
      searchValue: name || "",
      unitOfMeasureOptions: optionsToSelect,
      unitOfMeasure: defaultMeasure.value,
      isSearching: false,
    });
  };

  /**
   * Function to add a food
   */
  const handleAdd = (e) => {
    if (selectedItem) {
      const itemsToAdd = getItemsToAdd(
        selectedItem,
        quantity,
        unitOfMeasure,
        false
      );
      onAddItem(itemsToAdd);
      handleResetInputSearch(e);
    }
  };

  /**
   * Select the unit of measure
   * @param {*} newVal
   */
  const handleSelectChange = (newVal) => {
    updateState({ unitOfMeasure: newVal });
  };

  return (
    <div className="second-row">
      <div className="title">
        <span className="font-bold">
          {t("athlete-portal:foodActions.addFilter", { filter: "Food" })}
        </span>
      </div>
      <div className="inputs-container">
        <div className="w-full" ref={containerRef}>
          <div className="step">
            <span className="text">
              {t("athlete-portal:foodActions.searchAndAddFilter", {
                filter: "food",
              })}
            </span>
          </div>
          <div className="search-container">
            <input
              onFocus={() => updateState({ isSearching: true })}
              className={
                isSearching
                  ? "w-full! input-search pr-22!"
                  : "w-full! input-search"
              }
              placeholder={t(
                "athlete-portal:foodActions.searchFoodPlaceholder"
              )}
              value={searchValue}
              onChange={({ target: { value } }) =>
                updateState({
                  searchValue: value,
                })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch(e);
                }
              }}
              type="search"
            />
            <SaveInputButtons
              enabled={isSearching}
              handleSave={(e) => {
                handleSearch(e);
              }}
              handleDismiss={(e) => handleResetInputSearch(e)}
              saveDisabled={!searchValue || selectedItem}
              actionText={t("athlete-portal:foodActions.actionSearch")}
              showIcon={true}
            />
          </div>
          {isSearching && searchValue.length > 1 && (
            <FoodSearchResults
              isLoading={isLoading}
              data={data}
              error={error}
              onItemSelect={handleItemSelect}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={handleLoadMore}
            />
          )}
        </div>

        {(!selectedItem ||
          selectedItem?.type === "food" ||
          selectedItem?.type === "verified-food") && (
          <div className={unitOfMeasure !== "Select" ? "opacity-text" : ""}>
            <div className="step">
              <span className="text">
                {t("athlete-portal:foodActions.servingSize")}
              </span>
            </div>
            <Select
              withTruncate
              onChange={(newVal) => handleSelectChange(newVal)}
              name="unitOfMeasure"
              disabled={unitOfMeasureOptions.length < 2}
              value={unitOfMeasure}
              options={unitOfMeasureOptions.map(({ value, text }) => {
                return { value, text };
              })}
            />
          </div>
        )}

        {searchFilter && (
          <div>
            <div className="step">
              <span className="text">
                {t("athlete-portal:foodActions.numberOfServings")}
              </span>
            </div>
            <input
              onKeyDown={handleKeyDown}
              value={quantity || ""}
              onChange={({ target: { value } }) => {
                //Allow only 2 decimals
                if (/^\d*\.?\d{0,2}$/.test(value)) {
                  updateState({ quantity: value });
                }
              }}
              type="number"
              placeholder={t("athlete-portal:foodActions.servingsPlaceholder")}
            />
          </div>
        )}
        <div className="button-container">
          <button
            data-button-type={"outline"}
            onClick={handleAdd}
            disabled={!selectedItem || quantity <= 0}
          >
            <span>{t("common:add")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

FoodActionsForBuildMeal.propTypes = {
  locals: PropTypes.object,
  searchFilter: PropTypes.string,
  api: PropTypes.any,
  onAddItem: PropTypes.func,
};
